package com.fantasytenis.shared.domain.auth.entity

/**
 * User role for authorization.
 */
enum class UserRole {
    USER,
    ADMIN,
    SUPERADMIN;

    companion object {
        fun fromString(value: String): UserRole = when (value.lowercase()) {
            "admin" -> ADMIN
            "superadmin" -> SUPERADMIN
            else -> USER
        }
    }
}

/**
 * Avatar type for user profile.
 */
enum class AvatarType {
    NONE,
    PRESET,
    CUSTOM;

    companion object {
        fun fromString(value: String): AvatarType = when (value.lowercase()) {
            "preset" -> PRESET
            "custom" -> CUSTOM
            else -> NONE
        }
    }
}

/**
 * User domain entity.
 */
data class UserEntity(
    val id: String,
    val email: String,
    val firstName: String,
    val lastName: String,
    val phoneNumber: String? = null,
    val avatarUrl: String? = null,
    val kycStatus: KycStatus = KycStatus.PENDING,
    val role: UserRole = UserRole.USER,
    val createdAt: Long,
    val username: String? = null,
    val avatarType: AvatarType = AvatarType.NONE,
    val avatarPresetId: String? = null
) {
    val fullName: String get() = "$firstName $lastName"
    val displayName: String get() = username?.let { "@$it" } ?: fullName
    val isKycVerified: Boolean get() = kycStatus == KycStatus.VERIFIED
    val isAdmin: Boolean get() = role == UserRole.ADMIN || role == UserRole.SUPERADMIN
    val isSuperAdmin: Boolean get() = role == UserRole.SUPERADMIN
    val hasUsername: Boolean get() = username != null

    /**
     * Get the initials for avatar fallback display.
     */
    val initials: String get() = when {
        username != null -> username.take(2).uppercase()
        firstName.isNotBlank() && lastName.isNotBlank() -> "${firstName.first()}${lastName.first()}".uppercase()
        firstName.isNotBlank() -> firstName.take(2).uppercase()
        else -> "?"
    }
}

enum class KycStatus {
    PENDING,
    VERIFIED,
    FAILED
}
